import { useCallback, useMemo, useState } from 'react';
import { Button, message, Modal } from 'antd';
import {
  ExclamationCircleOutlined,
  EyeOutlined,
  EditOutlined,
  DeleteOutlined,
  AppstoreOutlined
} from '@ant-design/icons';
import {
  ErpDataGrid,
  ErpMasterShell,
  useErpMasterQuery
} from '../../../../components/common/erp-grid';
import { buildSupplierColumns } from './supplierColumns';
import { supplierApi } from './supplierApi';
import SupplierModal from './SupplierModal';
import SupplierGroupsModal from './SupplierGroupsModal';
import useApprovalMfa from '../../../../hooks/useApprovalMfa';

// ─────────────────────────────────────────────────────────────────
// Glass-i — Supplier Master page (Stage 6B.3).
//
// Mirrors CustomerMasterPage shape:
//   - Reusable ErpDataGrid with server-mode adapter
//   - Custom add/edit/view modal (SupplierModal) instead of grid's
//     built-in form modal
//   - Approval-MFA wired for delete + non-active status changes
//   - Right-click context menu (View / Edit / Delete) shares the same
//     handlers as the row action ⋯ menu and the inline action column
// ─────────────────────────────────────────────────────────────────

const ADAPTER = { list: (query) => supplierApi.list(query) };

function defaultOnError(err) {
  const msg = err?.response?.data?.error?.message ||
    err?.message || 'Failed to load suppliers.';
  message.error({ content: msg, key: 'supplier-list' });
}

export default function SupplierMasterPage() {
  const { gridHandlers, refetch, query } = useErpMasterQuery({
    adapter: ADAPTER, pageSize: 25, onError: defaultOnError
  });

  const currentPage = query && query.page     ? query.page     : 1;
  const pageSize    = query && query.pageSize ? query.pageSize : 25;
  const columns = useMemo(
    () => buildSupplierColumns({ currentPage, pageSize }),
    [currentPage, pageSize]
  );

  const { runWithApproval, ApprovalMfaModalSlot } = useApprovalMfa();

  const [drawerOpen, setDrawerOpen]   = useState(false);
  const [drawerMode, setDrawerMode]   = useState('add');
  const [drawerRecord, setDrawerRecord] = useState(null);

  // Stage 6B.3B — Supplier Groups CRUD popup.
  const [groupsOpen, setGroupsOpen] = useState(false);
  const openGroups  = useCallback(() => setGroupsOpen(true), []);
  const closeGroups = useCallback(() => setGroupsOpen(false), []);
  const onGroupsChanged = useCallback(() => refetch(), [refetch]);

  const toolbarExtra = useMemo(() => (
    <Button size="small" icon={<AppstoreOutlined />} onClick={openGroups}>
      Supplier Groups
    </Button>
  ), [openGroups]);

  // For Edit/View we want the FULL row (with linkedLedger + companions),
  // which the list endpoint doesn't carry. Fetch by id on open.
  const openAdd = useCallback(() => {
    setDrawerMode('add'); setDrawerRecord(null); setDrawerOpen(true);
  }, []);
  const openEditOrView = useCallback(async (record, mode) => {
    try {
      const full = await supplierApi.get(record.id);
      setDrawerMode(mode);
      setDrawerRecord(full);
      setDrawerOpen(true);
    } catch (e) {
      message.error(e?.response?.data?.error?.message || 'Failed to load supplier.');
    }
  }, []);
  const openEdit = useCallback((record) => openEditOrView(record, 'edit'), [openEditOrView]);
  const openView = useCallback((record) => openEditOrView(record, 'view'), [openEditOrView]);
  const closeDrawer = useCallback(() => setDrawerOpen(false), []);

  const onSaved = useCallback(async () => {
    setDrawerOpen(false);
    await refetch();
  }, [refetch]);

  const handleDelete = useCallback(async (record) => {
    const confirmed = await new Promise((resolve) => {
      let okClicked = false;
      Modal.confirm({
        title: 'Delete supplier?',
        icon: <ExclamationCircleOutlined />,
        content: `${record.supplierCode} · ${record.supplierName} will be soft-deleted and hidden from lists. Historic purchases / payments and the linked ledger are preserved. You will be asked for a 6-digit Approval MFA code to confirm.`,
        okText: 'Delete',
        okButtonProps: { danger: true },
        cancelText: 'Cancel',
        onOk: () => { okClicked = true; },
        onCancel: () => { okClicked = false; },
        afterClose: () => resolve(okClicked)
      });
    });
    if (!confirmed) return;
    try {
      await runWithApproval({
        operationKey:   'supplier.delete',
        resourceType:   'supplier',
        resourceId:     record.id,
        operationLabel: 'Delete supplier',
        resourceLabel:  `${record.supplierCode} · ${record.supplierName}`,
        action: (approvalMfaChallengeId) =>
          supplierApi.remove(record.id, { approvalMfaChallengeId })
      });
      message.success('Supplier deleted.');
      await refetch();
    } catch (err) {
      if (err && err.name === 'ApprovalMfaCancelled') return;
      const msg = err?.response?.data?.error?.message || 'Failed to delete supplier.';
      message.error(msg);
    }
  }, [refetch, runWithApproval]);

  const handleStatusChange = useCallback(async (record, status, reason) => {
    const needsApproval = status !== 'active';
    try {
      if (needsApproval) {
        await runWithApproval({
          operationKey:   'supplier.deactivate',
          resourceType:   'supplier',
          resourceId:     record.id,
          operationLabel: status === 'blocked' ? 'Block supplier' : 'Deactivate supplier',
          resourceLabel:  `${record.supplierCode} · ${record.supplierName}`,
          action: (approvalMfaChallengeId) =>
            supplierApi.updateStatus(record.id, { status, reason }, { approvalMfaChallengeId })
        });
      } else {
        await supplierApi.updateStatus(record.id, { status, reason });
      }
      message.success('Supplier status updated.');
      await refetch();
    } catch (err) {
      if (err && err.name === 'ApprovalMfaCancelled') return;
      message.error(err?.response?.data?.error?.message || 'Failed to update status.');
    }
  }, [refetch, runWithApproval]);

  const handleCellUpdate = useCallback(async (record, field, value) => {
    const fieldMap = {
      creditDays:       'credit_days',
      openingBalance:   'opening_balance',
      supplierCategory: 'supplier_category'
    };
    const backendField = fieldMap[field];
    if (!backendField) {
      message.error('Field is not editable inline.');
      throw new Error('inline edit not supported');
    }
    try {
      await supplierApi.updateCell(record.id, { field: backendField, value });
      message.success('Updated.', 1.5);
      await refetch();
    } catch (err) {
      message.error(err?.response?.data?.error?.message || 'Failed to update.');
      throw err;
    }
  }, [refetch]);

  const actionsProp     = useMemo(() => ({ add: true, view: true, edit: true, delete: true }), []);
  const pageSizeOptions = useMemo(() => ['15', '25', '50', '100'], []);

  const contextMenuActions = useMemo(() => ([
    { key: 'view',   label: 'View',   icon: <EyeOutlined />,    onClick: openView },
    { key: 'edit',   label: 'Edit',   icon: <EditOutlined />,   onClick: openEdit },
    { key: 'delete', label: 'Delete', icon: <DeleteOutlined />, danger: true,
      onClick: handleDelete }
  ]), [openView, openEdit, handleDelete]);

  return (
    <ErpMasterShell>
      <ErpDataGrid
        title="Supplier Master"
        gridId="supplier-master-v1"
        columns={columns}
        rowKey="id"
        rowClassName={(record) =>
          record && record.status === 'blocked' ? 'erp-grid__row--blocked' : ''
        }
        rowHeight="compact"
        stickyHeader
        selectable
        actionWidth={96}
        pageSizeOptions={pageSizeOptions}
        searchable
        searchPlaceholder="Search by code, name, GSTIN, mobile, email, city…"
        searchShortcut
        exportable
        exportFilename="supplier-master"
        actions={actionsProp}
        addButtonText="Add Supplier"
        emptyTitle="No suppliers yet"
        emptyText="Add your first supplier to start posting purchase bills, expenses and payments."
        emptyCtaLabel="Add Supplier"
        onAdd={openAdd}
        onEdit={openEdit}
        onView={openView}
        onRowClick={openView}
        onDelete={handleDelete}
        onUpdateCell={handleCellUpdate}
        contextMenuActions={contextMenuActions}
        toolbarExtra={toolbarExtra}
        {...gridHandlers}
      />
      <SupplierModal
        open={drawerOpen}
        mode={drawerMode}
        record={drawerRecord}
        onClose={closeDrawer}
        onSaved={onSaved}
        runWithApproval={runWithApproval}
      />
      <SupplierGroupsModal
        open={groupsOpen}
        onClose={closeGroups}
        onChanged={onGroupsChanged}
      />
      {ApprovalMfaModalSlot}
    </ErpMasterShell>
  );
}
